/**
 * Convert raw audit log entries into human-readable descriptions.
 *
 * The backend writes machine codes like `stripe.subscription_granted` plus
 * a JSON payload — useful for grep but opaque at a glance. This module owns
 * the action-key → Chinese description map so admin pages render something
 * an operator can scan without parsing JSON.
 *
 * Fallback: any unknown action renders the raw code with a generic icon,
 * so a missing entry never breaks the page — at worst it looks like the
 * old UI used to.
 *
 * Adding a new action: just append an entry below. Keep summaries short
 * (≤30 chars), put the important parameter at the end.
 */

export type AuditTone = 'success' | 'warning' | 'danger' | 'neutral' | 'admin' | 'system';

export interface AuditDescription {
  emoji: string;
  summary: string;
  tone: AuditTone;
}

interface FmtHelpers {
  fmtDate: (s: string | null | undefined) => string;
}

type Payload = Record<string, unknown> | null;
type Describer = (payload: Payload, fmt: FmtHelpers) => AuditDescription;

function planLabel(plan: unknown): string {
  if (plan === 'monthly') return '月卡';
  if (plan === 'yearly') return '年卡';
  return '未知套餐';
}

function str(v: unknown, fallback = '?'): string {
  return typeof v === 'string' && v !== '' ? v : fallback;
}

function scalar(v: unknown, fallback = '?'): string {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
    ? String(v)
    : fallback;
}

const DESCRIPTORS: Record<string, Describer> = {
  // ────────────────── user.* (终端用户行为) ──────────────────
  'user.register': () => ({ emoji: '✨', tone: 'neutral', summary: '新用户注册' }),
  'user.login': () => ({ emoji: '🔐', tone: 'neutral', summary: '用户登录' }),
  'user.login_failed': () => ({ emoji: '⚠️', tone: 'danger', summary: '用户登录失败' }),
  'user.email_verified': () => ({ emoji: '✉️', tone: 'success', summary: '邮箱验证通过' }),
  'user.redeem_code': (p) => ({
    emoji: '🎟️',
    tone: 'success',
    summary: `兑换码激活 → ${planLabel(p?.['plan'])}`,
  }),

  // ────────────────── admin.* (管理员操作) ──────────────────
  'admin.login': () => ({ emoji: '🔑', tone: 'admin', summary: '管理员登录' }),
  'admin.login_failed': () => ({ emoji: '⚠️', tone: 'danger', summary: '管理员登录失败' }),
  'admin.password_changed': () => ({ emoji: '🔑', tone: 'admin', summary: '管理员改密' }),
  'admin.user_password_reset': () => ({
    emoji: '🔁',
    tone: 'admin',
    summary: '重置用户密码',
  }),
  'admin.user_force_logout': () => ({
    emoji: '🚪',
    tone: 'admin',
    summary: '强制用户下线',
  }),
  'admin.user_force_verify': () => ({
    emoji: '✔️',
    tone: 'admin',
    summary: '强制验证用户邮箱',
  }),
  'admin.user_grant': (p, fmt) => ({
    emoji: '🎁',
    tone: 'admin',
    summary: `开通 ${planLabel(p?.['plan'])}，到期 ${fmt.fmtDate(p?.['new_expires_at'] as string | null | undefined)}`,
  }),
  'admin.create_xianyu_account': (p, fmt) => ({
    emoji: '🐟',
    tone: 'admin',
    summary: `闲鱼开户（${p?.['mode'] === 'email' ? '邮箱' : '账号'}）→ ${planLabel(p?.['plan'])}，到期 ${fmt.fmtDate(p?.['new_expires_at'] as string | null | undefined)}`,
  }),
  'admin.codes_generated': (p) => ({
    emoji: '🎟️',
    tone: 'admin',
    summary: `生成 ${scalar(p?.['count'])} 个 ${planLabel(p?.['plan'])} 兑换码${
      p?.['batch_id'] !== undefined && p['batch_id'] !== null ? `（批次 ${str(p['batch_id'])}）` : ''
    }`,
  }),
  'admin.codes_bulk_deleted': (p) => ({
    emoji: '🗑️',
    tone: 'admin',
    summary: `批量删除 ${scalar(p?.['deleted_count'])} 个兑换码`,
  }),
  'admin.renderer_draft_uploaded': (p) => ({
    emoji: '📦',
    tone: 'admin',
    summary: `上传热更新草稿 v${str(p?.['version'])}`,
  }),
  'admin.renderer_published': (p) => ({
    emoji: '🚀',
    tone: 'success',
    summary: `发布热更新 v${str(p?.['version'])}`,
  }),
  'admin.renderer_draft_discarded': () => ({
    emoji: '✂️',
    tone: 'admin',
    summary: '丢弃热更新草稿',
  }),
  'admin.feedback_ack': () => ({ emoji: '✅', tone: 'admin', summary: '标记反馈已处理' }),
  'admin.feedback_unack': () => ({
    emoji: '↺',
    tone: 'admin',
    summary: '反馈重新标为未处理',
  }),
  'admin.feedback_deleted': () => ({ emoji: '🗑️', tone: 'admin', summary: '删除反馈' }),

  // ────────────────── stripe.* (海外支付 webhook) ──────────────────
  'stripe.subscription_granted': (p, fmt) => ({
    emoji: '💳',
    tone: 'success',
    summary: `Stripe 付费 → 开通 ${planLabel(p?.['plan'])}，到期 ${fmt.fmtDate(p?.['new_expires_at'] as string | null | undefined)}`,
  }),
  'stripe.subscription_revoked': (p) => ({
    emoji: '⛔',
    tone: 'danger',
    summary: `Stripe 撤销订阅（${str(p?.['reason'])}）`,
  }),
  'stripe.payment_failed': () => ({
    emoji: '❌',
    tone: 'warning',
    summary: 'Stripe 支付失败（已交给 Stripe 自动重试）',
  }),
  'stripe.invoice_paid': () => ({
    emoji: '🧾',
    tone: 'system',
    summary: 'Stripe 续费成功',
  }),
  'stripe.checkout_unmapped': () => ({
    emoji: '⚠️',
    tone: 'warning',
    summary: 'Stripe checkout 无 user_id 映射 — 需要人工对账',
  }),
  'stripe.checkout_no_plan': () => ({
    emoji: '⚠️',
    tone: 'warning',
    summary: 'Stripe checkout 缺 plan 字段',
  }),
  'stripe.checkout_bad_plan': (p) => ({
    emoji: '⚠️',
    tone: 'warning',
    summary: `Stripe checkout plan 值非法: ${str(p?.['raw_plan'])}`,
  }),
  'stripe.refund_unmapped': () => ({
    emoji: '⚠️',
    tone: 'warning',
    summary: 'Stripe 退款无 user_id 映射',
  }),
  'stripe.user_not_found': () => ({
    emoji: '⚠️',
    tone: 'warning',
    summary: 'Stripe 关联用户在我们 DB 不存在',
  }),

  // ────────────────── discord.* (Bot) ──────────────────
  'discord.feedback_received': (p) => ({
    emoji: '💬',
    tone: 'system',
    summary: `Discord /feedback from @${str(p?.['discord_username'])}（${
      scalar(p?.['length'])
    } 字）`,
  }),
};

const FALLBACK: Describer = (_, __) => ({
  emoji: '📋',
  tone: 'neutral',
  summary: '',
});

export function describeAudit(
  action: string,
  payload: Payload,
  fmt: FmtHelpers,
): AuditDescription {
  const describer = DESCRIPTORS[action] ?? FALLBACK;
  const desc = describer(payload, fmt);
  // For the fallback, surface the raw key so admins can still tell rows apart.
  if (desc.summary === '') desc.summary = action;
  return desc;
}

/** Tailwind classes for each tone — chip/text colors. */
export const TONE_CLASSES: Record<AuditTone, string> = {
  success: 'text-emerald-300',
  warning: 'text-amber-300',
  danger: 'text-rose-300',
  neutral: 'text-slate-300',
  admin: 'text-shark-300',
  system: 'text-indigo-300',
};
